import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Brain, Sparkles, Shield, Network } from 'lucide-react';

const pillars = [
  { icon: Brain, label: 'IntentGraph', desc: 'Learns what you care about now', color: 'text-violet-400', bg: 'bg-violet-400/10' },
  { icon: Sparkles, label: 'Creator Engine', desc: 'AI tools that keep you in control', color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  { icon: Shield, label: 'Trust Engine', desc: 'Safety built in from day one', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  { icon: Network, label: 'Social Graph', desc: 'Connections that mean something', color: 'text-pink-400', bg: 'bg-pink-400/10' },
];

export default function AuthBrand() {
  return (
    <div className="hidden lg:flex flex-col justify-between w-[520px] xl:w-[580px] 2xl:w-[640px] flex-shrink-0 bg-card border-r border-border p-10 xl:p-14 relative overflow-hidden">
      {/* Background glow */}
      <div
        className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 20% 30%, #7c3aed 0%, transparent 70%)' }}
      />
      <div className="absolute inset-0 bg-grid-pattern opacity-40" />

      <div className="relative z-10">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 mb-12 group w-fit">
          <AppLogo size={36} />
          <span className="font-sans text-2xl font-800 tracking-widest text-foreground group-hover:text-accent transition-colors">
            NEXUS
          </span>
        </Link>

        {/* Headline */}
        <div className="mb-10">
          <div className="section-label mb-3 text-primary/70">The Intelligent Social Network</div>
          <h1 className="text-3xl xl:text-4xl font-800 text-foreground leading-tight mb-4">
            Connect around{' '}
            <span className="text-gradient-violet">what matters</span>{' '}
            to you right now.
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            NEXUS uses your current interests — not just your history — to surface the
            right people, ideas, and opportunities before you have to search for them.
          </p>
        </div>

        {/* Pillars */}
        <div className="grid grid-cols-1 gap-3">
          {pillars?.map((pillar) => (
            <div key={`auth-pillar-${pillar?.label}`} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-background/40">
              <div className={`w-9 h-9 rounded-lg ${pillar?.bg} flex items-center justify-center flex-shrink-0`}>
                <pillar.icon size={16} className={pillar?.color} />
              </div>
              <div>
                <div className="text-sm font-600 text-foreground">{pillar?.label}</div>
                <div className="text-xs text-muted-foreground">{pillar?.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom quote */}
      <div className="relative z-10 mt-8">
        <blockquote className="text-sm text-muted-foreground italic border-l-2 border-primary/40 pl-4">
          &ldquo;Help people spend less time searching and more time finding the right people,
          ideas, communities and opportunities.&rdquo;
        </blockquote>
        <div className="text-xs text-muted-foreground/60 mt-2 pl-4">— NEXUS Mission Statement</div>
      </div>
    </div>
  );
}